import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import Typography from "@mui/material/Typography";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import { Link } from "react-router-dom";
import GetAppIcon from "@mui/icons-material/GetApp";

import useCrew from "../../hooks/useCrew";
import Button from "../../components/Button";
import TableComponent from "../../components/TableComponent";
import { getCrewUsersTrainings } from "../../api/training.deprecated";
import { useShoppingCart } from "../../context/ShoppingCartContext";
import { openExportWindow } from "../../api/";

function formatDate(string, t) {
  if (string) {
    return new Date(string).toLocaleString("de-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  } else {
    return t("training_pages.missing");
  }
}

const CrewUsersTrainings = ({ match, history }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const onExportError = () => enqueueSnackbar(t("orders.document_not_found"), { variant: "error" });
  const { crew } = useCrew();
  const shoppingCart = useShoppingCart();
  const [crewUsersTrainings, setCrewUsersTrainings] = useState([]);

  useEffect(() => {
    (async () => {
      const res = await getCrewUsersTrainings(crew.id, crew.baseIds);
      setCrewUsersTrainings(res);
    })();
  }, [crew]);

  const addToCart = useCallback(
    (crewUsersTraining) => {
      const itemToAdd = {
        ...crewUsersTraining.trainingCourse,
        ...(crewUsersTraining.trainingHierarchy.highestCourseId && {
          trainingHierarchy: {
            ...crewUsersTraining.trainingCourse.trainingHierarchy,
            highestInTrainingHierarchyCourseId: crewUsersTraining.trainingHierarchy.highestCourseId,
            highestInTrainingHierarchyCourseName: crewUsersTraining.trainingHierarchy.highestCourseName,
          },
        }),
      };

      shoppingCart.actions.add(itemToAdd, crewUsersTraining.userId);
    },
    [shoppingCart.actions],
  );

  const getAddToCartTitle = useMemo(() => {
    return (crewUsersTraining) => {
      if (crewUsersTraining.trainingHierarchy.highestCourseId) {
        return t("training_pages.add_highest_training_to_cart");
      } else {
        const { id, displayName, price } = crewUsersTraining.trainingCourse;
        return t("training_pages.add_training_to_cart", {
          id,
          name: displayName,
          price: price ? `, ${price}€/CHF` : "",
        });
      }
    };
  }, [t]);

  return (
    <>
      <Typography variant="h4">{t("training_pages.crew_users_training")}</Typography>
      {crewUsersTrainings && (
        <TableComponent
          header={[
            { title: t("training_pages.user_id"), style: { width: "5%" } },
            { title: t("user") },
            { title: t("training_pages.training") },
            { title: t("position.name") },
            { title: t("valid_until") },
            { title: t("start_date") },
            { title: t("end_date") },
            { title: t("actions"), style: { width: "10%" } },
          ]}
          renderChildren={(crewUsersTraining) => (
            <TableRow key={crewUsersTraining.unique_key}>
              <TableCell>{crewUsersTraining.userId}</TableCell>
              <TableCell>{crewUsersTraining.fullname}</TableCell>
              <TableCell>{crewUsersTraining.trainingName}</TableCell>
              <TableCell>{crewUsersTraining.trainingPositions}</TableCell>
              <TableCell>{formatDate(crewUsersTraining.validUntil, t)}</TableCell>
              <TableCell>{formatDate(crewUsersTraining.trainingStartDate, t)}</TableCell>
              <TableCell>{formatDate(crewUsersTraining.trainingEndDate, t)}</TableCell>

              <TableCell>
                {crewUsersTraining.bought || crewUsersTraining.trainingHierarchy.boughtByCourseId ? (
                  <>
                    {!crewUsersTraining.hasCertificate && (
                      <Button
                        title={t("training_pages.training_bought", {
                          source: crewUsersTraining.trainingHierarchy.boughtByCourseId
                            ? t("training_pages.higher_training_hierarchy")
                            : `(${crewUsersTraining.trainingCourseId})`,
                        })}
                        icon={<AssignmentTurnedInIcon />}
                        onClick={() => {}}
                      />
                    )}
                    <Link
                      to={`/training-users/user/details/${crewUsersTraining.trainingHierarchy.highestTrainingId || crewUsersTraining.trainingId}/${crewUsersTraining.userId}/${crewUsersTraining.trainingHierarchy.boughtByCourseId || crewUsersTraining.trainingCourseId}`}
                    >
                      <Button
                        title={t("training_pages.checking_progress_for", {
                          source: crewUsersTraining.trainingHierarchy.boughtByCourseId
                            ? t("training_pages.higher_training_hierarchy")
                            : `(${crewUsersTraining.trainingCourseId})`,
                        })}
                        icon={<PublishedWithChangesIcon />}
                      />
                    </Link>
                    {crewUsersTraining.hasCertificate && (
                      <Button
                        title={t(
                          crewUsersTraining.trainingHierarchy.passedByCourseId
                            ? "training_pages.download_certificate_hierarchy"
                            : "training_pages.download_certificate",
                        )}
                        icon={<GetAppIcon />}
                        onClick={() =>
                          openExportWindow(crewUsersTraining.trainingCourseId, crewUsersTraining.userId, onExportError)
                        }
                      />
                    )}
                  </>
                ) : crewUsersTraining.hasCertificate ? (
                  <Button
                    title={t(
                      crewUsersTraining.trainingHierarchy.passedByCourseId
                        ? "training_pages.download_certificate_hierarchy"
                        : "training_pages.download_certificate",
                    )}
                    icon={<GetAppIcon />}
                    onClick={() =>
                      openExportWindow(crewUsersTraining.trainingCourseId, crewUsersTraining.userId, onExportError)
                    }
                  />
                ) : (
                  <Button
                    title={getAddToCartTitle(crewUsersTraining)}
                    icon={<AddShoppingCartIcon />}
                    onClick={() => addToCart(crewUsersTraining)}
                  />
                )}
              </TableCell>
            </TableRow>
          )}
          search={["userId", "fullname", "trainingName"]}
          pagination
          items={crewUsersTrainings}
        />
      )}
    </>
  );
};

export default CrewUsersTrainings;
